import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, Loader2, MessageCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface CustomerChatSheetProps {
  open: boolean;
  onClose: () => void;
  orderId: string;
  restaurantId: string;
  restaurantName: string;
  restaurantLogo: string | null;
  customerName: string | null;
  customerPhone: string | null;
  customerId: string | null;
  primaryColor: string;
  orderStatus: string;
  orderTotal: number;
  fmt: (v: number) => string;
}

interface ChatMessage {
  id: string;
  sender_type: string;
  message: string;
  created_at: string;
}

export const CustomerChatSheet = ({
  open, onClose, orderId, restaurantId, restaurantName, restaurantLogo,
  customerName, customerPhone, customerId, primaryColor,
  orderStatus, orderTotal, fmt,
}: CustomerChatSheetProps) => {
  const [chatId, setChatId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  // Initialize or find existing chat
  useEffect(() => {
    if (!open) return;
    const initChat = async () => {
      setLoading(true);
      try {
        // Check for existing chat for this order
        const { data: existingChats } = await supabase
          .from("restaurant_chats" as any)
          .select("*")
          .eq("order_id", orderId)
          .eq("restaurant_id", restaurantId)
          .order("created_at", { ascending: false })
          .limit(1) as any;

        if (existingChats && existingChats.length > 0) {
          const chat = existingChats[0];
          setChatId(chat.id);
          // Load messages
          const { data: msgs } = await supabase
            .from("restaurant_chat_messages" as any)
            .select("*")
            .eq("chat_id", chat.id)
            .order("created_at") as any;
          setMessages((msgs || []) as ChatMessage[]);
          // Mark customer messages as read
          await supabase
            .from("restaurant_chats" as any)
            .update({ unread_customer: 0 } as any)
            .eq("id", chat.id) as any;
        } else {
          // Create new chat
          const { data: newChat } = await supabase
            .from("restaurant_chats" as any)
            .insert({
              restaurant_id: restaurantId,
              order_id: orderId,
              customer_name: customerName,
              customer_phone: customerPhone,
              customer_id: customerId,
              status: "open",
              unread_restaurant: 1,
            } as any)
            .select()
            .single() as any;
          
          if (newChat) {
            setChatId(newChat.id);
            // Send automatic first message
            const autoMsg = `Olá! Gostaria de falar sobre meu pedido.`;
            const { data: firstMsg } = await supabase
              .from("restaurant_chat_messages" as any)
              .insert({
                chat_id: newChat.id,
                sender_type: "customer",
                message: autoMsg,
              } as any)
              .select()
              .single() as any;
            if (firstMsg) setMessages([firstMsg as ChatMessage]);
          }
        }
      } catch (err) {
        console.error("Chat init error:", err);
      } finally {
        setLoading(false);
      }
    };
    initChat();
  }, [open, orderId, restaurantId]);

  // Realtime messages subscription
  useEffect(() => {
    if (!chatId) return;
    const channel = supabase
      .channel(`customer-chat-${chatId}`)
      .on("postgres_changes", {
        event: "INSERT",
        schema: "public",
        table: "restaurant_chat_messages",
        filter: `chat_id=eq.${chatId}`,
      }, (payload) => {
        const newMsg = payload.new as ChatMessage;
        setMessages((prev) => {
          if (prev.find(m => m.id === newMsg.id)) return prev;
          return [...prev, newMsg];
        });
        // Mark as read if from restaurant
        if (newMsg.sender_type === "restaurant") {
          supabase
            .from("restaurant_chats" as any)
            .update({ unread_customer: 0 } as any)
            .eq("id", chatId) as any;
        }
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [chatId]);

  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);

  const handleSend = async () => {
    if (!newMessage.trim() || !chatId || sending) return;
    const text = newMessage.trim();
    setNewMessage("");
    setSending(true);
    try {
      await supabase.from("restaurant_chat_messages" as any).insert({
        chat_id: chatId,
        sender_type: "customer",
        message: text,
      } as any);
      // Increment unread for restaurant
      const currentUnread = messages.filter(m => m.sender_type === "customer").length + 1;
      await (supabase.from("restaurant_chats" as any)
        .update({ unread_restaurant: currentUnread, updated_at: new Date().toISOString() } as any)
        .eq("id", chatId) as any);
    } catch (err) {
      console.error("Send error:", err);
      setNewMessage(text);
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const statusLabels: Record<string, string> = {
    received: "Recebido", preparing: "Em preparo", ready: "Pronto",
    out_for_delivery: "Saiu para entrega", completed: "Entregue", cancelled: "Cancelado",
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/50 flex items-end md:items-center justify-center"
        onClick={onClose}
      >
        <motion.div
          initial={{ y: "100%" }}
          animate={{ y: 0 }}
          exit={{ y: "100%" }}
          transition={{ type: "spring", damping: 28, stiffness: 300 }}
          className="w-full max-w-lg h-[85vh] md:h-[70vh] bg-white md:rounded-2xl rounded-t-[20px] flex flex-col overflow-hidden shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center gap-3 px-4 py-3 border-b border-[hsl(0,0%,93%)]" style={{ backgroundColor: primaryColor }}>
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center overflow-hidden shrink-0">
              {restaurantLogo ? (
                <img src={restaurantLogo} alt="" className="w-full h-full object-cover" />
              ) : (
                <MessageCircle className="h-5 w-5 text-white" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-sm text-white truncate">{restaurantName}</p>
              <p className="text-[11px] text-white/70">
                Pedido #{orderId.slice(0, 8).toUpperCase()} • {statusLabels[orderStatus] || orderStatus}
              </p>
            </div>
            <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/15 flex items-center justify-center">
              <X className="h-4 w-4 text-white" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-auto p-4 space-y-3 bg-[hsl(0,0%,97%)]">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="h-6 w-6 animate-spin" style={{ color: primaryColor }} />
              </div>
            ) : (
              <>
                {/* Order context card */}
                <div className="bg-white rounded-xl p-3 border border-[hsl(0,0%,92%)] text-center">
                  <p className="text-xs text-[hsl(0,0%,55%)]">
                    Chat sobre o pedido <strong>#{orderId.slice(0, 8).toUpperCase()}</strong>
                  </p>
                  <p className="text-xs text-[hsl(0,0%,55%)] mt-0.5">
                    Status: {statusLabels[orderStatus]} • Total: {fmt(orderTotal)}
                  </p>
                </div>

                {messages.map((msg) => {
                  const isCustomer = msg.sender_type === "customer";
                  return (
                    <motion.div
                      key={msg.id}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`flex ${isCustomer ? "justify-end" : "justify-start"}`}
                    >
                      <div
                        className={`max-w-[80%] px-3.5 py-2.5 rounded-2xl text-sm ${
                          isCustomer
                            ? "text-white rounded-br-md"
                            : "bg-white text-[hsl(0,0%,13%)] border border-[hsl(0,0%,90%)] rounded-bl-md"
                        }`}
                        style={isCustomer ? { backgroundColor: primaryColor } : undefined}
                      >
                        <p className="whitespace-pre-wrap break-words">{msg.message}</p>
                        <p className={`text-[10px] mt-1 ${isCustomer ? "text-white/60" : "text-[hsl(0,0%,60%)]"}`}>
                          {new Date(msg.created_at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
                        </p>
                      </div>
                    </motion.div>
                  );
                })}
                <div ref={messagesEndRef} />
              </>
            )}
          </div>

          {/* Input */}
          <div className="p-3 border-t border-[hsl(0,0%,93%)] bg-white flex items-center gap-2">
            <input
              ref={inputRef}
              type="text"
              placeholder="Digite sua mensagem..."
              className="flex-1 h-10 rounded-full bg-[hsl(0,0%,96%)] px-4 text-sm text-[hsl(0,0%,13%)] placeholder:text-[hsl(0,0%,55%)] outline-none"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSend()}
            />
            <button
              onClick={handleSend}
              disabled={!newMessage.trim() || sending}
              className="w-10 h-10 rounded-full flex items-center justify-center text-white disabled:opacity-40 transition-all active:scale-90"
              style={{ backgroundColor: primaryColor }}
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
